import React, { useState, useEffect, useMemo } from 'react';
import { splynxApi, customersApi, billingApi } from '../services/api';
import { SplynxStatus, SplynxCustomerRow, SplynxCustomerDetail, SplynxPackage, Customer } from '../types';
import toast from 'react-hot-toast';
import './Splynx.css';

const fmtDate = (d?: string | null) => {
  if (!d) return null;
  const dt = new Date(`${d}T00:00:00`);
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
};
const since = (d?: string | null) => {
  if (!d) return '';
  const days = Math.floor((Date.now() - new Date(`${d}T00:00:00`).getTime()) / 86400000);
  if (!isFinite(days) || days < 0) return '';
  if (days < 60) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30.44);
  if (months < 24) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(days / 365.25);
  const rem = Math.floor((days - years * 365.25) / 30.44);
  return `${years} yr${years === 1 ? '' : 's'}${rem ? ` ${rem} mo` : ''} ago`;
};
const rand = (n: number) =>
  `R ${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type StatusFilter = 'all' | 'active' | 'new' | 'inactive';
type MapFilter = 'all' | 'mapped' | 'unmapped';

const STATUS_LABEL: Record<string, string> = {
  active: 'Active', new: 'New', blocked: 'Blocked', disabled: 'Disabled',
};

// Manually hidden Splynx customers (per browser, same pattern as Invoice Prep)
const HIDDEN_KEY = 'cv-splynx-hidden-customers';

function getHiddenIds(): Set<number> {
  try {
    const stored = localStorage.getItem(HIDDEN_KEY);
    if (stored) return new Set(JSON.parse(stored));
  } catch {}
  return new Set();
}

function saveHiddenIds(ids: Set<number>) {
  try { localStorage.setItem(HIDDEN_KEY, JSON.stringify(Array.from(ids))); } catch {}
}

function Splynx() {
  const [status, setStatus] = useState<SplynxStatus | null>(null);
  const [rows, setRows] = useState<SplynxCustomerRow[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [autoMapping, setAutoMapping] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [mapFilter, setMapFilter] = useState<MapFilter>('all');
  const [hiddenIds, setHiddenIds] = useState<Set<number>>(getHiddenIds);
  const [showHidden, setShowHidden] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Row expansion (service / billed-line detail)
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<SplynxCustomerDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editPrice, setEditPrice] = useState<{ productId: number; value: string } | null>(null);
  // Selling prices (price book): per fibre package (default) or per client
  const [packages, setPackages] = useState<SplynxPackage[]>([]);
  const [pkgOpen, setPkgOpen] = useState(true);
  const [editSell, setEditSell] = useState<{ key: string; value: string; clientOnly: boolean } | null>(null);
  const loadPackages = async () => {
    try { setPackages(await splynxApi.getPackages()); } catch { /* not fatal */ }
  };
  const saveSell = async (name: string, value: string, clientOnly: boolean, customerId?: number | null) => {
    const price = value.trim() === '' ? null : value.trim();
    try {
      if (clientOnly && customerId) {
        const r = await billingApi.setClientPrice({ service_type: 'splynx', item_name: name, customer_id: customerId, selling_price: price });
        toast.success(r.removed ? 'Client price removed - back to the package price' : `Client price set: ${rand(parseFloat(price!))}`);
      } else {
        await billingApi.updatePriceBookItem({ service_type: 'splynx', item_name: name, selling_price: price });
        toast.success(price === null ? 'Selling price cleared - billed at cost' : `Selling price set for every customer on ${name}`);
      }
      setEditSell(null);
      await loadPackages();
      if (expandedId != null) { try { setDetail(await splynxApi.getCustomerDetail(expandedId)); } catch {} }
      loadData();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to save the selling price');
    }
  };

  // Map modal
  const [mapRow, setMapRow] = useState<SplynxCustomerRow | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [mapPrice, setMapPrice] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // Bulk modal
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusData, customerData, rowData] = await Promise.all([
        splynxApi.getStatus().catch(() => ({ connected: false, error: 'Not configured' } as SplynxStatus)),
        customersApi.list(),
        splynxApi.getCustomers().catch(() => [] as SplynxCustomerRow[]),
      ]);
      setStatus(statusData);
      setCustomers(customerData);
      setRows(rowData);
    } catch {
      toast.error('Failed to load Splynx data');
    } finally {
      setLoading(false);
    }
  };

  const reloadRows = async () => {
    try {
      setRows(await splynxApi.getCustomers());
    } catch {
      toast.error('Failed to reload');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await splynxApi.sync();
      const kinds = r.service_kinds || {};
      const ok = Object.keys(kinds).filter(k => kinds[k] === 'ok');
      const missing = Object.keys(kinds).filter(k => kinds[k] === 'forbidden');
      const svc = ok.length === 0
        ? 'MRR mode (API key has no services permission yet)'
        : `${r.services_synced} service lines (${ok.join(', ')})`;
      toast.success(`Synced ${r.synced} Splynx customers — ${svc}`);
      if (missing.length > 0 && ok.length > 0) {
        toast(`No API permission for: ${missing.join(', ')} services`, { icon: '🔒' });
      }
      if (r.errors?.length) toast.error(`${r.errors.length} error(s) during sync`);
      await loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleAutoMap = async () => {
    setAutoMapping(true);
    try {
      const r = await splynxApi.autoMap();
      toast.success(`Auto-map: ${r.matched} matched (${r.created} new), ${r.unmatched} unmatched`);
      reloadRows();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Auto-map failed');
    } finally {
      setAutoMapping(false);
    }
  };

  const openMapModal = (row: SplynxCustomerRow) => {
    setMapRow(row);
    setSelectedCustomer(row.customer_id);
    setCustomerSearch('');
    setMapPrice('');
  };

  const handleMap = async () => {
    if (!mapRow || !selectedCustomer) {
      toast.error('Select a customer');
      return;
    }
    setSaving(true);
    try {
      await splynxApi.mapCustomer(mapRow.id, selectedCustomer, mapPrice || undefined);
      toast.success('Mapped');
      setMapRow(null);
      reloadRows();
      if (expandedId === mapRow.id) loadDetail(mapRow.id);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to map');
    } finally {
      setSaving(false);
    }
  };

  const handleUnmap = async (row: SplynxCustomerRow) => {
    try {
      await splynxApi.unmapCustomer(row.id);
      toast.success('Mapping removed');
      reloadRows();
      if (expandedId === row.id) loadDetail(row.id);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to unmap');
    }
  };

  const toggleRow = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const handleBulkMap = async () => {
    if (selected.size === 0 || !selectedCustomer) {
      toast.error('Select rows and a customer');
      return;
    }
    setSaving(true);
    try {
      const r = await splynxApi.bulkMap(Array.from(selected), selectedCustomer);
      toast.success(`Mapped ${r.mapped} to ${r.customer}`);
      setBulkOpen(false);
      setSelected(new Set());
      setSelectedCustomer(null);
      reloadRows();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Bulk map failed');
    } finally {
      setSaving(false);
    }
  };

  const loadDetail = async (rowId: number) => {
    setDetailLoading(true);
    try {
      setDetail(await splynxApi.getCustomerDetail(rowId));
    } catch {
      setDetail(null);
      toast.error('Failed to load detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleExpand = (row: SplynxCustomerRow) => {
    if (expandedId === row.id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(row.id);
    setDetail(null);
    setEditPrice(null);
    loadDetail(row.id);
  };

  const savePriceOverride = async (productId: number, value: string) => {
    try {
      const r = await splynxApi.updateItemPricing(productId, value.trim() || undefined);
      toast.success(r.price_override ? 'Price overridden' : 'Reverted to Splynx price');
      setEditPrice(null);
      if (expandedId) loadDetail(expandedId);
      reloadRows();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update price');
    }
  };

  const toggleHideRow = (row: SplynxCustomerRow) => {
    const next = new Set(hiddenIds);
    if (next.has(row.id)) {
      next.delete(row.id);
      toast.success(`${row.name} unhidden`);
    } else {
      next.add(row.id);
      toast.success(`${row.name} hidden`);
    }
    setHiddenIds(next);
    saveHiddenIds(next);
  };

  const unhideAll = () => {
    setHiddenIds(new Set());
    saveHiddenIds(new Set());
    setShowHidden(false);
    toast.success('All Splynx customers unhidden');
  };

  // ── Derived ──
  const filteredRows = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(r => {
      if (showHidden ? !hiddenIds.has(r.id) : hiddenIds.has(r.id)) return false;
      const st = (r.status || '').toLowerCase();
      const statusOk =
        statusFilter === 'all' ||
        (statusFilter === 'active' && st === 'active') ||
        (statusFilter === 'new' && st === 'new') ||
        (statusFilter === 'inactive' && (st === 'disabled' || st === 'blocked'));
      const mapOk =
        mapFilter === 'all' ||
        (mapFilter === 'mapped' && r.customer_id) ||
        (mapFilter === 'unmapped' && !r.customer_id);
      const searchOk =
        !q ||
        (r.name || '').toLowerCase().includes(q) ||
        (r.login || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.customer_name || '').toLowerCase().includes(q);
      return statusOk && mapOk && searchOk;
    });
  }, [rows, statusFilter, mapFilter, search, hiddenIds, showHidden]);

  const hiddenCount = useMemo(() => rows.filter(r => hiddenIds.has(r.id)).length, [rows, hiddenIds]);

  const stats = useMemo(() => {
    // Summary cards cover the non-hidden universe — hiding declutters the workspace
    const universe = rows.filter(r => !hiddenIds.has(r.id));
    let active = 0, mapped = 0, mrr = 0, billable = 0;
    for (const r of universe) {
      if ((r.status || '').toLowerCase() === 'active') {
        active++;
        mrr += r.mrr_total || 0;
      }
      if (r.customer_id) {
        mapped++;
        billable += r.monthly_total || 0;
      }
    }
    return { total: universe.length, active, mapped, unmapped: universe.length - mapped, mrr, billable };
  }, [rows, hiddenIds]);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.toLowerCase();
    return customers.filter(c => !q || c.xero_name.toLowerCase().includes(q)).slice(0, 100);
  }, [customers, customerSearch]);

  const unmappedInView = filteredRows.filter(r => !r.customer_id);
  const allUnmappedSelected = unmappedInView.length > 0 && unmappedInView.every(r => selected.has(r.id));
  const toggleSelectAll = () => {
    if (allUnmappedSelected) setSelected(new Set());
    else setSelected(new Set(unmappedInView.map(r => r.id)));
  };

  useEffect(() => { loadPackages(); }, [rows.length]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="spx-page"><div className="spx-loading">Loading Splynx data…</div></div>;
  }

  return (
    <div className="spx-page">
      <div className="spx-header">
        <div>
          <h1>Splynx</h1>
          <p className="spx-subtitle">Map Splynx (ISP billing) customers to CloudView clients and bill their services</p>
        </div>
        <div className="spx-header-actions">
          <button className="spx-btn ghost" onClick={handleSync} disabled={syncing || !status?.connected}>
            {syncing ? '⟳ Syncing…' : '⟳ Sync from Splynx'}
          </button>
          <button className="spx-btn primary" onClick={handleAutoMap} disabled={autoMapping || rows.length === 0}>
            {autoMapping ? '🔗 Matching…' : '🔗 Auto-Map by Name'}
          </button>
        </div>
      </div>

      <div className={`spx-status ${status?.connected ? 'connected' : 'disconnected'}`}>
        <span className="spx-status-dot" />
        <strong>{status?.connected ? 'Connected' : 'Not connected'}</strong>
        {status?.connected && <span className="spx-status-sub">{rows.length} customers cached{rows.length === 0 ? ' — run a sync' : ''}</span>}
        {status?.error && <span className="spx-status-err">{status.error}</span>}
      </div>

      {rows.length > 0 && (
        <>
          {/* Summary */}
          <div className="spx-summary">
            <div className="spx-card"><div className="spx-card-val">{stats.total}</div><div className="spx-card-lbl">Splynx customers</div></div>
            <div className="spx-card"><div className="spx-card-val">{stats.active}</div><div className="spx-card-lbl">Active</div></div>
            <div className="spx-card ok"><div className="spx-card-val">{stats.mapped}</div><div className="spx-card-lbl">Mapped</div></div>
            <div className="spx-card warn"><div className="spx-card-val">{stats.unmapped}</div><div className="spx-card-lbl">Unmapped</div></div>
            <div className="spx-card"><div className="spx-card-val">{rand(stats.mrr)}</div><div className="spx-card-lbl">Splynx MRR (active)</div></div>
            <div className="spx-card total"><div className="spx-card-val">{rand(stats.billable)}</div><div className="spx-card-lbl">Billable / month</div></div>
          </div>

          {/* Fibre packages: cost from Splynx, selling price ours */}
          <div className="spx-packages">
            <div className="spx-packages-head" onClick={() => setPkgOpen(o => !o)}>
              <span className={`spx-caret ${pkgOpen ? 'open' : ''}`}>▸</span>
              <strong>Fibre packages &amp; pricing</strong>
              <span className="spx-sub">{packages.length} packages · cost = Splynx reseller price · click Sell to set what we bill (applies to every customer on that package; per-client prices in the row detail)</span>
            </div>
            {pkgOpen && (
              <table className="spx-mini-table spx-pkg-table">
                <thead><tr><th>Package</th><th className="spx-num">Lines</th><th className="spx-num">Customers</th><th className="spx-num">Cost (Splynx)</th><th className="spx-num">Sell</th><th className="spx-num">Margin / line</th></tr></thead>
                <tbody>
                  {packages.length === 0 && <tr><td colSpan={6} className="spx-detail-empty">No mapped lines yet - map customers and the packages appear here.</td></tr>}
                  {packages.map(p => (
                    <tr key={p.name}>
                      <td>{p.name}{p.client_prices > 0 && <span className="spx-sub"> · {p.client_prices} client price{p.client_prices === 1 ? '' : 's'}</span>}</td>
                      <td className="spx-num">{p.lines}</td>
                      <td className="spx-num">{p.customers}</td>
                      <td className="spx-num" title={p.cost_min !== p.cost_max ? `Splynx prices vary: ${rand(p.cost_min || 0)} - ${rand(p.cost_max || 0)}` : ''}>{rand(p.cost)}{p.cost_min !== p.cost_max ? ' *' : ''}</td>
                      <td className="spx-num">
                        {editSell?.key === `pkg:${p.name}` ? (
                          <input className="spx-price-input" autoFocus type="number" step="0.01" min="0" value={editSell.value}
                                 onChange={e => setEditSell({ ...editSell, value: e.target.value })}
                                 onKeyDown={e => { if (e.key === 'Enter') saveSell(p.name, editSell.value, false); if (e.key === 'Escape') setEditSell(null); }}
                                 onBlur={() => setEditSell(null)} placeholder="blank = at cost" />
                        ) : (
                          <span className={`spx-sell-click ${p.sell ? 'set' : 'unset'}`} title="Click to set the selling price for this package"
                                onClick={() => setEditSell({ key: `pkg:${p.name}`, value: p.sell || '', clientOnly: false })}>
                            {p.sell ? rand(parseFloat(p.sell)) : 'set…'}
                          </span>
                        )}
                      </td>
                      <td className={`spx-num ${p.margin != null && p.margin < 0 ? 'spx-neg' : ''}`}>{p.margin == null ? '—' : rand(p.margin)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Filters */}
          <div className="spx-toolbar">
            <div className="spx-chip-group">
              {(['all', 'active', 'new', 'inactive'] as StatusFilter[]).map(f => (
                <button key={f} className={statusFilter === f ? 'active' : ''} onClick={() => setStatusFilter(f)}>
                  {f === 'all' ? 'All statuses' : f === 'active' ? 'Active' : f === 'new' ? 'New' : 'Disabled/Blocked'}
                </button>
              ))}
            </div>
            <div className="spx-chip-group">
              {(['all', 'mapped', 'unmapped'] as MapFilter[]).map(f => (
                <button key={f} className={mapFilter === f ? 'active' : ''} onClick={() => setMapFilter(f)}>
                  {f === 'all' ? 'All' : f === 'mapped' ? `Mapped (${stats.mapped})` : `Unmapped (${stats.unmapped})`}
                </button>
              ))}
            </div>
            {(hiddenCount > 0 || showHidden) && (
              <button
                className={`spx-toggle ${showHidden ? 'on' : ''}`}
                onClick={() => setShowHidden(!showHidden)}
                title={showHidden ? 'Back to the normal view' : 'View the hidden customers'}
              >
                🙈 Hidden ({hiddenCount})
              </button>
            )}
            {showHidden && hiddenCount > 0 && (
              <button className="spx-btn ghost" onClick={unhideAll}>Unhide all</button>
            )}
            <input
              className="spx-search"
              placeholder="Search name, login, email, mapped customer…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {selected.size > 0 && (
              <button className="spx-btn primary" onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); setBulkOpen(true); }}>
                Map {selected.size} selected
              </button>
            )}
          </div>

          {/* Table */}
          <div className="spx-table-wrap">
            <table className="spx-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input type="checkbox" checked={allUnmappedSelected} onChange={toggleSelectAll} title="Select all unmapped in view" />
                  </th>
                  <th>Splynx Customer</th>
                  <th>Status</th>
                  <th>Services</th>
                  <th className="spx-num">Splynx MRR</th>
                  <th>Mapped Customer</th>
                  <th className="spx-num" title="What we bill (price book) / what Splynx charges us">Sell / Cost per mo</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr><td colSpan={8} className="spx-empty">No Splynx customers match the current filters.</td></tr>
                ) : filteredRows.map(row => {
                  const mapped = !!row.customer_id;
                  const st = (row.status || '').toLowerCase();
                  const expanded = expandedId === row.id;
                  return (
                    <React.Fragment key={row.id}>
                      <tr className={`${mapped ? 'mapped' : ''}${showHidden ? ' hidden-view' : ''}${row.has_billables === false ? ' no-billables' : ''}`}
                          title={row.has_billables === false ? 'No billable service and no MRR in Splynx - nothing is billed, no lines in Invoice Prep' : undefined}>
                        <td>
                          {!mapped && (
                            <input type="checkbox" checked={selected.has(row.id)} onChange={() => toggleRow(row.id)} />
                          )}
                        </td>
                        <td>
                          <div className="spx-name" onClick={() => toggleExpand(row)} title="Show services">
                            <span className={`spx-caret ${expanded ? 'open' : ''}`}>▸</span>
                            {row.has_billables === false && <span className="spx-gone-tag" title="Nothing billable in Splynx">nothing billable · not billed</span>}
                            {row.name}
                          </div>
                          <div className="spx-sub">{row.login}{row.city ? ` · ${row.city}` : ''}</div>
                        </td>
                        <td><span className={`spx-badge st-${st}`}>{STATUS_LABEL[st] || row.status}</span></td>
                        <td>
                          {row.active_services > 0
                            ? <span className="spx-svc-count">{row.active_services} active</span>
                            : <span className="spx-svc-mrr" title="Per-service detail needs the API key's services permission">MRR</span>}
                        </td>
                        <td className="spx-num">{rand(row.mrr_total)}</td>
                        <td>
                          {row.customer_name
                            ? <span className="spx-match ok">{row.customer_name}</span>
                            : <span className="spx-match no">Not mapped</span>}
                        </td>
                        <td className="spx-num">
                          {mapped ? (
                            <>
                              <strong className="spx-money">{rand(row.monthly_sell ?? row.monthly_total)}</strong>
                              <div className="spx-sub">cost {rand(row.monthly_total)}</div>
                            </>
                          ) : '–'}
                        </td>
                        <td>
                          <div className="spx-row-actions">
                            {mapped ? (
                              <>
                                <button className="spx-btn xs" onClick={() => openMapModal(row)}>Edit</button>
                                <button className="spx-btn xs danger" onClick={() => handleUnmap(row)}>Unmap</button>
                              </>
                            ) : (
                              <button className="spx-btn xs primary" onClick={() => openMapModal(row)}>Map</button>
                            )}
                            <button
                              className="spx-btn xs hide"
                              title={hiddenIds.has(row.id) ? 'Unhide customer' : 'Hide customer'}
                              onClick={() => toggleHideRow(row)}
                            >
                              {hiddenIds.has(row.id) ? '👁️' : '🙈'}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="spx-detail-row">
                          <td colSpan={8}>
                            {detailLoading ? (
                              <div className="spx-detail-loading">Loading…</div>
                            ) : detail ? (
                              <>
                              <div className="spx-detail-dates">
                                <span className="spx-date-chip" title="date_add on the Splynx customer record">
                                  <span className="spx-date-k">Created in Splynx</span>
                                  {detail.date_add ? <><strong>{fmtDate(detail.date_add)}</strong> <span className="spx-date-ago">{since(detail.date_add)}</span></> : <em>unknown</em>}
                                </span>
                                <span className="spx-date-chip" title="Earliest start date over this customer's Splynx services">
                                  <span className="spx-date-k">First service added</span>
                                  {detail.first_service_date ? (
                                    <><strong>{fmtDate(detail.first_service_date)}</strong> <span className="spx-date-ago">{since(detail.first_service_date)}</span>
                                    {detail.first_service && <span className="spx-date-what"> · {detail.first_service}</span>}</>
                                  ) : <em>{detail.services.length ? 'no start date on the services' : 'no service detail synced'}</em>}
                                </span>
                                {detail.date_add && detail.first_service_date && detail.first_service_date < detail.date_add && (
                                  <span className="spx-date-note" title="Splynx lets a service start before the customer record was created (imported history)">service predates the customer record</span>
                                )}
                              </div>
                              <div className="spx-detail">
                                <div className="spx-detail-col">
                                  <h4>Splynx services</h4>
                                  {detail.services.length === 0 ? (
                                    <p className="spx-detail-empty">
                                      No service detail available — billing falls back to the customer's
                                      Splynx MRR ({rand(detail.mrr_total)}). Grant the API key
                                      Customers → services permissions in Splynx and re-sync for per-service lines.
                                    </p>
                                  ) : (
                                    <table className="spx-mini-table">
                                      <thead><tr><th>Kind</th><th>Description</th><th>Status</th><th title="Service start date in Splynx">Started</th><th title="Service end date in Splynx (blank = open-ended)">Ends</th><th className="spx-num">Qty</th><th className="spx-num">Price</th></tr></thead>
                                      <tbody>
                                        {detail.services.map(s => (
                                          <tr key={`${s.kind}-${s.id}`} className={(s.status || '').toLowerCase() === 'active' ? '' : 'inactive'}>
                                            <td><span className={`spx-kind kind-${s.kind}`}>{s.kind}</span></td>
                                            <td>{s.description}</td>
                                            <td>{s.status}</td>
                                            <td className="spx-date" title={s.start_date ? since(s.start_date) : ''}>{fmtDate(s.start_date) || '—'}</td>
                                            <td className="spx-date">{fmtDate(s.end_date) || '—'}</td>
                                            <td className="spx-num">{s.quantity}</td>
                                            <td className="spx-num">{rand(s.unit_price)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                                <div className="spx-detail-col">
                                  <h4>Billed lines {detail.billed_lines.length === 0 && <span className="spx-detail-hint">(map this customer to create them)</span>}</h4>
                                  {detail.billed_lines.length > 0 && (
                                    <table className="spx-mini-table">
                                      <thead><tr><th>Line</th><th className="spx-num">Qty</th><th className="spx-num" title="Splynx reseller price (our cost) - click to override">Cost (Splynx)</th><th className="spx-num" title="Price book: client price > package default > cost">Sell</th><th className="spx-num">Monthly (sell)</th></tr></thead>
                                      <tbody>
                                        {detail.billed_lines.map(l => (
                                          <tr key={l.product_id}>
                                            <td>{l.name}{l.price_override && <span className="spx-override-dot" title="Manual price override — synced Splynx price ignored"> ●</span>}</td>
                                            <td className="spx-num">{l.quantity}</td>
                                            <td className="spx-num">
                                              {editPrice?.productId === l.product_id ? (
                                                <input
                                                  className="spx-price-input"
                                                  autoFocus
                                                  type="number"
                                                  step="0.01"
                                                  value={editPrice.value}
                                                  onChange={e => setEditPrice({ productId: l.product_id, value: e.target.value })}
                                                  onKeyDown={e => {
                                                    if (e.key === 'Enter') savePriceOverride(l.product_id, editPrice.value);
                                                    if (e.key === 'Escape') setEditPrice(null);
                                                  }}
                                                  onBlur={() => setEditPrice(null)}
                                                  placeholder="blank = Splynx cost"
                                                />
                                              ) : (
                                                <span className="spx-price-click" title="Our cost from Splynx - click to override (blank reverts to the Splynx price)"
                                                  onClick={() => setEditPrice({ productId: l.product_id, value: l.unit_price || '' })}>
                                                  {rand(parseFloat(l.unit_price || '0'))}
                                                </span>
                                              )}
                                            </td>
                                            <td className="spx-num">
                                              {editSell?.key === `line:${l.product_id}` ? (
                                                <span className="spx-sell-edit">
                                                  <input className="spx-price-input" autoFocus type="number" step="0.01" min="0" value={editSell.value}
                                                         onChange={e => setEditSell({ ...editSell, value: e.target.value })}
                                                         onKeyDown={e => { if (e.key === 'Enter') saveSell(l.name, editSell.value, editSell.clientOnly, detail.customer_id); if (e.key === 'Escape') setEditSell(null); }}
                                                         placeholder="blank = clear" />
                                                  <label className="spx-client-only" title="Only this customer pays this price; otherwise it becomes the package price for everyone">
                                                    <input type="checkbox" checked={editSell.clientOnly} onChange={e => setEditSell({ ...editSell, clientOnly: e.target.checked })} /> this client only
                                                  </label>
                                                  <button className="spx-btn xs primary" onClick={() => saveSell(l.name, editSell.value, editSell.clientOnly, detail.customer_id)}>Save</button>
                                                  <button className="spx-btn xs" onClick={() => setEditSell(null)}>Cancel</button>
                                                </span>
                                              ) : (
                                                <span className={`spx-sell-click ${l.price_source === 'client' || l.price_source === 'default' ? 'set' : 'unset'}`}
                                                      title={l.price_source === 'client' ? 'Client-specific price - click to change' : l.price_source === 'default' ? 'Package price (all customers) - click to change or set a client price' : 'No selling price yet - this line is NOT billed until you set one (the Splynx cost is never billed)'}
                                                      onClick={() => setEditSell({ key: `line:${l.product_id}`, value: l.sell_price && l.price_source !== 'unpriced' ? l.sell_price : '', clientOnly: l.price_source === 'client' })}>
                                                  {l.price_source === 'client' || l.price_source === 'default' ? `${rand(parseFloat(l.sell_price || '0'))}${l.price_source === 'client' ? ' 👤' : ''}` : 'set…'}
                                                </span>
                                              )}
                                            </td>
                                            <td className="spx-num"><strong className="spx-money">{rand(l.monthly_sell ?? l.monthly_total)}</strong>{l.margin != null && l.margin !== 0 && <div className={`spx-sub ${l.margin < 0 ? 'spx-neg' : ''}`}>margin {rand(l.margin * l.quantity)}</div>}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                              </div>
                              </>
                            ) : (
                              <div className="spx-detail-loading">No detail available</div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Map / Edit Modal */}
      {mapRow && (
        <div className="spx-modal-overlay" onClick={() => setMapRow(null)}>
          <div className="spx-modal" onClick={e => e.stopPropagation()}>
            <div className="spx-modal-head">
              <h3>{mapRow.customer_id ? 'Edit mapping' : 'Map Splynx customer'}</h3>
              <button className="spx-x" onClick={() => setMapRow(null)}>✕</button>
            </div>
            <div className="spx-modal-body">
              <div className="spx-info">
                <strong>{mapRow.name}</strong>
                <span>{mapRow.login} · {STATUS_LABEL[(mapRow.status || '').toLowerCase()] || mapRow.status} · Splynx MRR {rand(mapRow.mrr_total)}</span>
              </div>
              <label className="spx-field"><span>Search customer</span>
                <input autoFocus placeholder="Type to search…" value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} />
              </label>
              <div className="spx-customer-list">
                {filteredCustomers.map(c => (
                  <div key={c.id} className={`spx-customer ${selectedCustomer === c.id ? 'selected' : ''}`} onClick={() => setSelectedCustomer(c.id)}>
                    {c.xero_name}
                  </div>
                ))}
                {filteredCustomers.length === 0 && <div className="spx-customer empty">No customers found</div>}
              </div>
              <label className="spx-field"><span>Billed price override (optional — blank bills the Splynx price{mapRow.active_services === 0 ? `, ${rand(mapRow.mrr_total)}/mo` : ''})</span>
                <input type="number" min="0" step="0.01" value={mapPrice} onChange={e => setMapPrice(e.target.value)} placeholder={mapRow.active_services === 0 ? String(mapRow.mrr_total) : 'per-line prices from Splynx'} />
              </label>
            </div>
            <div className="spx-modal-foot">
              <button className="spx-btn ghost" onClick={() => setMapRow(null)}>Cancel</button>
              <button className="spx-btn primary" onClick={handleMap} disabled={!selectedCustomer || saving}>
                {saving ? 'Saving…' : mapRow.customer_id ? 'Save' : 'Map'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Modal */}
      {bulkOpen && (
        <div className="spx-modal-overlay" onClick={() => setBulkOpen(false)}>
          <div className="spx-modal" onClick={e => e.stopPropagation()}>
            <div className="spx-modal-head"><h3>Map {selected.size} Splynx customers</h3><button className="spx-x" onClick={() => setBulkOpen(false)}>✕</button></div>
            <div className="spx-modal-body">
              <div className="spx-info">
                <strong>{selected.size} selected</strong>
                <span>{rows.filter(r => selected.has(r.id)).slice(0, 4).map(r => r.name).join(', ')}{selected.size > 4 ? ` +${selected.size - 4} more` : ''}</span>
              </div>
              <label className="spx-field"><span>Search customer</span>
                <input autoFocus placeholder="Type to search…" value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} />
              </label>
              <div className="spx-customer-list">
                {filteredCustomers.map(c => (
                  <div key={c.id} className={`spx-customer ${selectedCustomer === c.id ? 'selected' : ''}`} onClick={() => setSelectedCustomer(c.id)}>
                    {c.xero_name}
                  </div>
                ))}
              </div>
            </div>
            <div className="spx-modal-foot">
              <button className="spx-btn ghost" onClick={() => setBulkOpen(false)}>Cancel</button>
              <button className="spx-btn primary" onClick={handleBulkMap} disabled={!selectedCustomer || saving}>
                {saving ? 'Saving…' : `Map ${selected.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Splynx;
